import gql from 'graphql-tag'
import {useQuery} from 'react-apollo'
import {Client} from '../../graphql/types'
import {PAMM_OFFER_TREE_FIELDS} from '../PammOffersTree/pammOfferFragment'

/**
 * Fetches the agent's PAMM offers for the hierarchical tree view.
 *
 * `includeDownline: true` widens the backend scope from the agent's directly-owned offers
 * to their whole downline (every nested sub-agent offer below them), which is what the
 * tree needs in order to render the agent's full structure.
 */
const ACCOUNTS_WITH_PAMM_AGENT_OFFERS = gql`
  query AccountsWithPammAgentOffers {
    viewer {
      ... on Client {
        id
        accounts(hidden: false, rejected: false) {
          ... on BaseAccount {
            pammAgentOffers(includeDownline: true) {
              ...PammOfferTreeFields
            }
          }
        }
      }
    }
  }
  ${PAMM_OFFER_TREE_FIELDS}
`

const isNotNil = <T>(value: T): value is NonNullable<T> => {
  if (typeof value === 'undefined') return false
  if (value === null) return false
  return true
}

export const usePammAgentOffers = () => {
  const {loading, data, error} = useQuery<{viewer: Client}>(ACCOUNTS_WITH_PAMM_AGENT_OFFERS)

  const pammAgentOffers =
    (data?.viewer?.accounts ?? [])
      .map((account) => account?.pammAgentOffers)
      .flat()
      .filter(isNotNil) ?? []

  return {error, loading, pammAgentOffers}
}
